import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import Shepherd, { Tour as ShepherdTour } from 'shepherd.js';
import { TranslateService } from '@ngx-translate/core';
import { TOUR_REGISTRY } from './tour.registry';
import { AppTourStep, TourHandlers } from './tour.model';
import { offset } from '@floating-ui/dom';

const TOUR_PAUSE_STORAGE_KEY = 'tour_pause_snapshots';
const TOUR_COMPLETED_KEY = 'tour_completed';

interface PauseSnapshot {
  tourId: string;
  stepId: string;
}

@Injectable({ providedIn: 'root' })
export class TourService {

  private tour!: ShepherdTour;
  private handlers: TourHandlers = {};
  private outsideClickHandler!: (event: MouseEvent) => void;
  private showProgress = false;
  private currentTourId: string | null = null;

  private pausedHandlersMap: Record<string, TourHandlers> = {};
  private parallelTours: ShepherdTour[] = [];
  private parallelOutsideClickHandler!: (event: MouseEvent) => void;

  constructor(private translate: TranslateService, private router: Router) {}

  // ───────────────── SNAPSHOT STORAGE ─────────────────

  private getSnapshots(): Record<string, PauseSnapshot> {
    try {
      const raw = localStorage.getItem(TOUR_PAUSE_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  private saveSnapshot(snapshot: PauseSnapshot) {
    const all = this.getSnapshots();
    all[snapshot.tourId] = snapshot;
    localStorage.setItem(TOUR_PAUSE_STORAGE_KEY, JSON.stringify(all));
  }

  private getSnapshot(tourId: string): PauseSnapshot | null {
    return this.getSnapshots()[tourId] ?? null;
  }

  private clearSnapshot(tourId: string) {
    const all = this.getSnapshots();
    if (!all[tourId]) return;
    delete all[tourId];
    localStorage.setItem(TOUR_PAUSE_STORAGE_KEY, JSON.stringify(all));
    delete this.pausedHandlersMap[tourId];
  }

  private clearAllSnapshots() {
    localStorage.removeItem(TOUR_PAUSE_STORAGE_KEY);
    this.pausedHandlersMap = {};
  }

  // ───────────────── COMPLETION STORAGE ─────────────────

  private getCompletedTours(): string[] {
    try {
      const raw = localStorage.getItem(TOUR_COMPLETED_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  private markCompleted(tourId: string) {
    const all = this.getCompletedTours();
    if (!all.includes(tourId)) {
      all.push(tourId);
      localStorage.setItem(TOUR_COMPLETED_KEY, JSON.stringify(all));
    }
  }

  private clearCompleted(tourId: string) {
    const all = this.getCompletedTours().filter(id => id !== tourId);
    localStorage.setItem(TOUR_COMPLETED_KEY, JSON.stringify(all));
  }

  isCompleted(tourId: string): boolean {
    return this.getCompletedTours().includes(tourId);
  }

  // ───────────────── PUBLIC API ─────────────────

  async start(tourId: string, handlers: TourHandlers = {}) {

    const snapshot = this.getSnapshot(tourId);

    if (snapshot) {
      this.pausedHandlersMap[tourId] = { ...handlers };
      await this.resume(tourId);
      return;
    }

    // Auto-pause running tour
    if (this.tour && this.currentTourId) {
      this.pause();
      this.tour.cancel();
      this.removeOutsideClickListener();
    }

    this.handlers = handlers;
    this.currentTourId = tourId;

    const tourConfig = TOUR_REGISTRY[tourId];
    if (!tourConfig) return;

    this.showProgress = tourConfig.showProgress ?? false;

    this.tour = new Shepherd.Tour({
      useModalOverlay: tourConfig.useModalOverlay ?? true,
      defaultStepOptions: {
        scrollTo: true,
        cancelIcon: { enabled: true },
        ...(tourConfig.defaultStepOptions || {}),
        floatingUIOptions: {
          middleware: [
            offset({ mainAxis: 20, crossAxis: 0 })
          ]
        }
      }
    });

    this.registerLifecycleEvents();

    const totalSteps = tourConfig.steps.length;

    for (let i = 0; i < tourConfig.steps.length; i++) {
      await this.addStep(tourConfig.steps[i], i, totalSteps);
    }

    this.tour.start();

    if (tourConfig.allowOutsideClick) {
      this.attachOutsideClickListener();
    }
  }

  pause(resumeFromStepId?: string) {

    if (!this.currentTourId || !this.tour) return;

    const currentStep = this.tour.getCurrentStep();

    let stepId = '';

    if (resumeFromStepId) {
      stepId = resumeFromStepId;
    } else if (currentStep) {
      const currentIndex = this.tour.steps.indexOf(currentStep);
      const nextStep = this.tour.steps[currentIndex + 1];
      stepId = nextStep?.id ?? currentStep.id;
    }

    this.saveSnapshot({
      tourId: this.currentTourId,
      stepId
    });

    this.pausedHandlersMap[this.currentTourId] = { ...this.handlers };
    this.tour.hide();
    this.removeOutsideClickListener();
  }

pauseAfterCurrent() {

  if (!this.currentTourId || !this.tour) return;

  const currentStep = this.tour.getCurrentStep();
  if (!currentStep) return;

  const tourConfig = TOUR_REGISTRY[this.currentTourId];
  if (!tourConfig) return;

  const currentIndex = this.tour.steps.findIndex(s => s.id === currentStep.id);
  const nextStep = this.tour.steps[currentIndex + 1];

  if (!nextStep) {
    this.tour.complete();
    return;
  }

  this.saveSnapshot({
    tourId: this.currentTourId,
    stepId: nextStep.id
  });

  this.pausedHandlersMap[this.currentTourId] = { ...this.handlers };

  this.tour.hide();
  this.removeOutsideClickListener();
}

  async resume(tourId?: string, handlers?: TourHandlers) {

    const id = tourId ?? this.currentTourId;
    if (!id) return;

    const snapshot = this.getSnapshot(id);
    if (!snapshot) return;

    const tourConfig = TOUR_REGISTRY[id];
    if (!tourConfig) return;

    if (this.tour && this.currentTourId && this.currentTourId !== id) {
      this.pause();
      this.tour.cancel();
      this.removeOutsideClickListener();
    }

    this.handlers = handlers ?? this.pausedHandlersMap[id] ?? {};
    this.currentTourId = id;
    this.showProgress = tourConfig.showProgress ?? false;

    const startIndex = tourConfig.steps.findIndex(s => s.id === snapshot.stepId);
    const resumeFromIndex = startIndex === -1 ? 0 : startIndex;

    this.tour = new Shepherd.Tour({
      useModalOverlay: tourConfig.useModalOverlay ?? true,
      defaultStepOptions: {
        scrollTo: true,
        cancelIcon: { enabled: true },
        ...(tourConfig.defaultStepOptions || {}),
        floatingUIOptions: {
          middleware: [
            offset({ mainAxis: 20, crossAxis: 0 })
          ]
        }
      }
    });

    this.registerLifecycleEvents();

    const totalSteps = tourConfig.steps.length;

    for (let i = resumeFromIndex; i < tourConfig.steps.length; i++) {
      await this.addStep(tourConfig.steps[i], i, totalSteps);
    }

    this.tour.start();

    if (tourConfig.allowOutsideClick) {
      this.attachOutsideClickListener();
    }
  }

  stop(tourId?: string) {
    const id = tourId ?? this.currentTourId;
    if (!id) return;

    if (this.currentTourId === id) {
      this.tour?.cancel();
      this.removeOutsideClickListener();
      this.currentTourId = null;
    }

    this.clearSnapshot(id);
    this.clearCompleted(id);
  }

  stopAll() {
    this.tour?.cancel();
    this.removeOutsideClickListener();
    this.clearAllSnapshots();
    localStorage.removeItem(TOUR_COMPLETED_KEY);
    this.currentTourId = null;
  }

  isPaused(tourId: string): boolean {
    return !!this.getSnapshot(tourId);
  }

  next() { this.tour?.next(); }
  back() { this.tour?.back(); }
  complete() { this.tour?.complete(); }

  // ───────────────── LIFECYCLE ─────────────────

  private registerLifecycleEvents() {

    this.tour.on('show', ({ step }: { step: any }) => {
      if (!this.currentTourId) return;

      this.saveSnapshot({ tourId: this.currentTourId, stepId: step.id });
    });

    this.tour.on('complete', () => {
      if (this.currentTourId) {
        this.clearSnapshot(this.currentTourId);
        this.markCompleted(this.currentTourId);
      }
      this.removeOutsideClickListener();
      this.currentTourId = null;
    });

    this.tour.on('cancel', () => {
      this.removeOutsideClickListener();
      this.currentTourId = null;
    });
  }

  // ───────────────── STEP BUILDER ─────────────────

  private async addStep(step: AppTourStep, stepIndex: number, totalSteps: number) {

    if (step.route) {
      await this.router.navigateByUrl(step.route);
      await this.waitForElement(step.attachTo?.element as string);
    }

    const buttons = step.buttons?.map(btn => ({
      text: this.translate.instant(btn.text),
      classes: btn.className || 'shepherd-button-primary',
      action: () => {
        switch (btn.action) {
          case 'next':
            this.tour.next();
            break;
          case 'back':
            this.tour.back();
            break;
          case 'cancel':
            this.tour.cancel();
            break;
          case 'custom':
            if (btn.handlerKey && this.handlers[btn.handlerKey]) {
              this.handlers[btn.handlerKey]();
            } else if (btn.handler) {
              btn.handler();
            }
            break;
        }
      }
    }));

    this.tour.addStep({
      id: step.id,
      title: step.title ? this.translate.instant(step.title) : undefined,
      text: step.text ? this.translate.instant(step.text) : undefined,
      attachTo: step.attachTo,
      cancelIcon: step.cancelIcon ?? undefined,
      canClickTarget: step.canClickTarget ?? false,
      buttons,
      stepIndex: stepIndex,
      totalSteps: totalSteps,

      when: this.showProgress ? {
        show: function () {
          const stepInstance: any = this;

          const current = stepInstance.options.stepIndex + 1;
          const total = stepInstance.options.totalSteps;
         // const percent = Math.round((current / total) * 100);
          const percent = Math.round(((current - 1) / total) * 100);

          const stepEl = stepInstance.el;
          const footer = stepEl?.querySelector('.shepherd-footer');
          if (!stepEl || !footer) return;

          const existing = stepEl.querySelector('.shepherd-progress');
          if (existing) existing.remove();

          const progressContainer = document.createElement('div');
          progressContainer.className = 'shepherd-progress';
          progressContainer.innerHTML = `
            <div class="tour-progress-wrapper">
              <div class="tour-progress-label">
                <b>${current}</b> of ${total} Step(s)
              </div>
              <div class="tour-progress-bar">
                <div class="tour-progress-fill" style="width:${percent}%"></div>
              </div>
            </div>
          `;
          footer.parentNode?.insertBefore(progressContainer, footer);
        }
      } : undefined

    } as any);
  }

private waitForElement(selector: string): Promise<void> {
  return new Promise(resolve => {
    const maxAttempts = 50;
    let attempts = 0;

    const interval = setInterval(() => {
      if (document.querySelector(selector) || attempts >= maxAttempts) {
        clearInterval(interval);
        resolve();
      }
      attempts++;
    }, 200);
  });
}

  private attachOutsideClickListener(): void {
    setTimeout(() => {
      this.outsideClickHandler = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const shepherdEl = document.querySelector('.shepherd-element');
        if (shepherdEl && shepherdEl.contains(target)) return;
        if (target.closest('.shepherd-modal-overlay-container')) return;
        this.tour?.cancel();
      };
      document.addEventListener('click', this.outsideClickHandler, true);
    }, 200);
  }

  private removeOutsideClickListener(): void {
  if (this.outsideClickHandler) {
    document.removeEventListener('click', this.outsideClickHandler, true);
    this.outsideClickHandler = undefined as any;
  }
}

  // ───────────────── PARALLEL TOURS ─────────────────

  async showParallelFromRegistry(tourId: string) {
    this.destroyParallelPopups();

    const tourConfig = TOUR_REGISTRY[tourId];
    if (!tourConfig) return;

    const stepsToShow = tourConfig.steps.filter(s => s.parallel);

    for (const stepConfig of stepsToShow) {
      if (!stepConfig.attachTo?.element) continue;

      await this.waitForElement(stepConfig.attachTo.element as string);

      const tour = new Shepherd.Tour({
        useModalOverlay: false,
        defaultStepOptions: tourConfig?.defaultStepOptions ?? {
          scrollTo: false,
          cancelIcon: { enabled: true },
        }
      });

      tour.addStep({
        id: stepConfig.id,
        title: stepConfig.title ? this.translate.instant(stepConfig.title) : undefined,
        text: stepConfig.text ? this.translate.instant(stepConfig.text) : undefined,
        attachTo: stepConfig.attachTo,
        cancelIcon: stepConfig.cancelIcon ?? undefined,
        floatingUIOptions: {
          middleware: [
            offset({ mainAxis: 20, crossAxis: 0 })
          ]
        },
        buttons: []
      });

      const stepInstance = tour.getById(stepConfig.id);
      stepInstance?.show();

      this.parallelTours.push(tour);
    }

    this.attachParallelOutsideClickListener();
  }

  private attachParallelOutsideClickListener(): void {
    setTimeout(() => {
      this.parallelOutsideClickHandler = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        if (target.closest('.shepherd-element')) return;
        this.destroyParallelPopups();
      };
      document.addEventListener('click', this.parallelOutsideClickHandler, true);
    }, 100);
  }

  destroyParallelPopups() {
    this.parallelTours.forEach(t => t.complete());
    this.parallelTours = [];
  }
}